<template>
  <footer class="bg-gray-900 text-white pt-16 pb-8 font-sans antialiased" v-if="settings">
    <div class="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
        <!-- Company Info -->
        <div>
          <h2 class="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400 mb-6">
            {{ settings.strings.app_name }}
          </h2>
          <p class="text-gray-400 text-sm leading-relaxed mb-6">
            {{ settings.strings.website.websiteFooterBrief }}
          </p>
          <div class="flex space-x-4">
            <a
              v-if="settings.strings.website.social?.fbLink"
              :href="settings.strings.website.social.fbLink"
              target="_blank"
              class="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-blue-600 transition-colors duration-300"
            >
              <img src="/img/icons/facebook.png" class="w-5 h-5 filter invert opacity-80" alt="Facebook" />
            </a>
            <a
              v-if="settings.strings.website.social?.igLink"
              :href="settings.strings.website.social.igLink"
              target="_blank"
              class="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-pink-600 transition-colors duration-300"
            >
              <img src="/img/icons/instagram.png" class="w-5 h-5 filter invert opacity-80" alt="Instagram" />
            </a>
            <a
              v-if="settings.strings.website.social?.twLink"
              :href="settings.strings.website.social.twLink"
              target="_blank"
              class="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-blue-400 transition-colors duration-300"
            >
              <img src="/img/icons/twitter.png" class="w-5 h-5 filter invert opacity-80" alt="Twitter" />
            </a>
          </div>
        </div>

        <!-- Links Column 1 -->
        <div>
          <h3 class="text-lg font-semibold mb-6 text-gray-100">{{ $t('footer.header1') }}</h3>
          <ul class="space-y-3">
             <li>
              <a :href="`${base_url}pages/contact`" class="text-gray-400 hover:text-white transition-colors duration-200 block">
                {{ $t('footer.sub1') }}
              </a>
            </li>
            <li>
              <a :href="`${base_url}privacy/policy`" class="text-gray-400 hover:text-white transition-colors duration-200 block">
                {{ $t('footer.sub2') }}
              </a>
            </li>
            <li>
              <a :href="`${base_url}pages/terms`" class="text-gray-400 hover:text-white transition-colors duration-200 block">
                {{ $t('footer.sub3') }}
              </a>
            </li>
          </ul>
        </div>

        <!-- Links Column 2 -->
        <div>
          <h3 class="text-lg font-semibold mb-6 text-gray-100">{{ $t('footer.header2') }}</h3>
          <ul class="space-y-3">
             <li>
              <a :href="`${base_url}register/driver`" class="text-gray-400 hover:text-white transition-colors duration-200 block">
                {{ $t('footer.sub4') }}
              </a>
            </li>
            <li>
              <a :href="`${base_url}register/vendor`" class="text-gray-400 hover:text-white transition-colors duration-200 block">
                {{ $t('footer.sub5') }}
              </a>
            </li>
          </ul>
        </div>

         <!-- Links Column 3 (Categories) -->
        <div>
          <h3 class="text-lg font-semibold mb-6 text-gray-100">{{ $t('footer.header5') }}</h3>
          <ul class="space-y-3">
             <li v-for="vendor in vendors" :key="vendor.id">
              <a 
                :href="$router.resolve({ name: 'VendorHome', params: { id: vendor.id, slug: sanitizeTitle(vendor.slug || vendor.name) } }).href"
                class="text-gray-400 hover:text-white transition-colors duration-200 block"
              >
                {{ vendor.name }}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div class="border-t border-gray-800 pt-8 mt-8 flex flex-col md:flex-row justify-between items-center">
        <p class="text-gray-500 text-sm">
          &copy; {{ new Date().getFullYear() }} {{ settings.strings.app_name }}. {{ $t('footer.copyright2') }}
        </p>
         <div class="mt-4 md:mt-0 flex space-x-6">
             <!-- Payment Icons Placeholder -->
             <div class="flex space-x-2 grayscale opacity-50">
                 <div class="w-10 h-6 bg-gray-700 rounded"></div>
                 <div class="w-10 h-6 bg-gray-700 rounded"></div>
                 <div class="w-10 h-6 bg-gray-700 rounded"></div>
             </div>
         </div>
      </div>
    </div>
  </footer>
</template>

<script>
import axios from "axios";

export default {
  name: "FooterView",
  data() {
    return {
      settings: null,
      vendors: [],
      base_url: this.$store.state.baseUrl,
    };
  },

  mounted() {
    this.$store.dispatch("fetchSettings").then(() => {
      this.settings = this.$store.state.settings;
    });
    this.fetchVendors();
  },

  methods: {
    fetchVendors() {
        let selectedLanguage = localStorage.getItem("selectedLanguage");
        const lan = selectedLanguage || "en";
        axios
          .get(this.base_url + "api/vendor/types?lan=" + lan)
          .then((response) => {
            this.vendors = response.data.slice(0, 5);
          })
          .catch((error) => console.log(error));
    },
    sanitizeTitle(title) {
      if (!title) return "";
      let slug = title.toLowerCase();
      slug = slug.replace(/e|é|è|ẽ|ẻ|ẹ|ê|ế|ề|ễ|ể|ệ/gi, "e");
      slug = slug.replace(/a|á|à|ã|ả|ạ|ă|ắ|ằ|ẵ|ẳ|ặ|â|ấ|ầ|ẫ|ẩ|ậ/gi, "a");
      slug = slug.replace(/o|ó|ò|õ|ỏ|ọ|ô|ố|ồ|ỗ|ổ|ộ|ơ|ớ|ờ|ỡ|ở|ợ/gi, "o");
      slug = slug.replace(/u|ú|ù|ũ|ủ|ụ|ư|ứ|ừ|ữ|ử|ự/gi, "u");
      slug = slug.replace(/đ/gi, "d");
      slug = slug.replace(/\s*$/g, "");
      slug = slug.replace(/\s+/g, "-");
      return slug;
    },
  },
};
</script>
